import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { BehaviorSubject, combineLatestWith, filter, startWith, Subject, takeUntil } from "rxjs";

import { Organization } from "../../organizations/models/organizations.entity";
import { Checkbox } from "../../shared/components/checkmarks/checkmarks.component";
import { Sherpa } from "../../users/models/sherpa.entity";
import { Role } from "../../users/models/users.entity";
import { ProfileService } from "../../users/services/profile.service";
import { mediumPillarColors, PillarSlug } from "../colors";
import { DynamicType } from "../models/dynamic.model";
import { Thematic } from "../models/thematic.model";
import { ThematicRepository } from "../repositories/thematic.repository";


@Component({
  selector: 'app-sherpa-thematics',
  templateUrl: './sherpa-thematics.component.html',
  styleUrl: './sherpa-thematics.component.scss'
})
export class SherpaThematicsComponent implements OnInit, OnDestroy {
  loading = true;

  thematics!: Thematic[];

  organization!: Organization;

  selectedThematicIds: string[] = [];

  destroy$ = new Subject<void>();

  selectedThematicIds$!: BehaviorSubject<string[]>;

  pillarCheckboxes: Checkbox<PillarSlug>[] = [
    { id: 'body', key: 'resources.pillars.body', selected: false },
    { id: 'mind', key: 'resources.pillars.mind', selected: false },
    { id: 'relations', key: 'resources.pillars.relations', selected: false },
    { id: 'posture', key: 'resources.pillars.posture', selected: false },
  ];

  onlyMyThematics = new FormControl<boolean>(false, { nonNullable: true });

  pillarCheckboxesSelected$: BehaviorSubject<Checkbox<PillarSlug>[]> = new BehaviorSubject<Checkbox<PillarSlug>[]>([]);

  dynamicTypeCheckboxes: Checkbox<DynamicType>[] = [ {
    id: DynamicType.COACHING,
    key: "dynamics.coaching",
    selected: false,
  },
    {
      id: DynamicType.SPEECH,
      key: "dynamics.speech",
      selected: false,
    } ];

  selectionThematicCheckboxes: { thematic: Thematic, checked: boolean }[] = [];

  shownThematics: { thematic: Thematic, checked: boolean }[] = [];

  dynamicTypeCheckboxesSelected$: BehaviorSubject<Checkbox<DynamicType>[]> = new BehaviorSubject<Checkbox<DynamicType>[]>([]);

  constructor(private readonly router: Router,
              private readonly thematicRepository: ThematicRepository,
              private readonly profileService: ProfileService,
  ) {
  }

  ngOnInit() {
    this.dynamicTypeCheckboxesSelected$
      .pipe(combineLatestWith(this.pillarCheckboxesSelected$, this.onlyMyThematics.valueChanges.pipe(startWith(false))),
        takeUntil(this.destroy$))
      .subscribe(([ dynamicCheckboxes, pillarCheckboxes, onlyMyThematics ]) => {
        const dynamicTypes = dynamicCheckboxes.filter(c => c.selected).map(c => c.id);
        const pillarsChosen = pillarCheckboxes.filter(c => c.selected).map(c => c.id);

        this.shownThematics = this.selectionThematicCheckboxes
            ?.filter(checkbox =>
              (!dynamicTypes.length || dynamicTypes.some(type => checkbox.thematic.dynamics.map(d => d.type).includes(type)))
              && (!pillarsChosen.length || pillarsChosen.includes(checkbox.thematic.pillar?.name as PillarSlug)))
            ?.filter(checkbox => checkbox.checked || !onlyMyThematics)
          ?? [];
      });


    this.thematicRepository.getAvailableOnes()
      .pipe(
        combineLatestWith(
          this.profileService.getObservable()
            .pipe(filter((profile): profile is Sherpa => profile.role === Role.Sherpa)),
        ),
        takeUntil(this.destroy$))
      .subscribe(([ thematics, profile ]) => {
        this.thematics = thematics;
        const dynamicAuthorizedIds = profile.dynamicsAuthorized?.map(d => d.id) ?? [];
        this.selectedThematicIds = thematics.filter(thematic => thematic.dynamics.some(d => dynamicAuthorizedIds.includes(d.id))).map(t => t.id);
        this.selectedThematicIds$ = new BehaviorSubject<string[]>(this.selectedThematicIds);

        this.selectionThematicCheckboxes = this.thematics.map(thematic => ({
          thematic,
          checked: this.selectedThematicIds.includes(thematic.id)
        }));


        this.shownThematics = this.selectionThematicCheckboxes;

      });

  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  selectPillar(slug: PillarSlug) {
    const pillar = this.pillarCheckboxes.find(p => p.id === slug);
    if (pillar) {
      pillar.selected = !pillar.selected;
    }
    this.pillarCheckboxesSelected$.next(this.pillarCheckboxes.filter(checkbox => checkbox.selected));
  }

  selectDynamic(type: string) {
    const dynamic = this.dynamicTypeCheckboxes.find(c => c.id === type);
    if (dynamic) {
      dynamic.selected = !dynamic.selected;
    }
    this.dynamicTypeCheckboxesSelected$.next(this.dynamicTypeCheckboxes.filter(checkbox => checkbox.selected));
  }

  getPillarColor(thematic: Thematic): string {
    const pillarName = thematic.pillar?.name as PillarSlug;
    return pillarName ? mediumPillarColors[pillarName] : 'white';
  }

  goToThematic(thematic: Thematic) {
    this.router.navigate([ 'thematics', thematic.slug ]);
  }

  protected readonly DynamicType = DynamicType;

  protected readonly mediumPillarColors = mediumPillarColors;
}
